/**
 * Demo-mode anonymizer: deep-transforms API response bodies so customer-identifying
 * values (names, contact details, addresses, identifiers, and locations) are replaced
 * with deterministic pseudonyms while all other data — especially numeric and
 * time-series values — passes through untouched.
 *
 * Applied by `anonymizingFetchBaseQuery` before responses enter the RTK Query cache,
 * so every downstream consumer (components, selectors, the IndexedDB DataManager,
 * DataGrid CSV exports, QuickSearch) sees only anonymized data.
 *
 * This is presentation-layer masking for live demos, not a security boundary: the real
 * data still reaches the browser and is visible in the network tab.
 */
import {
  chargerName,
  cityName,
  descriptionText,
  domainName,
  emailAddress,
  firstName,
  fnv1a,
  genericLabel,
  lastName,
  macAddress,
  organizationName,
  personName,
  phoneNumber,
  postalCode,
  siteName,
  streetAddress,
  token,
  url,
  vehicleName,
  vin
} from './pseudonyms'

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}/g
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/**
 * All-lowercase identifiers with at least one separator, e.g. `vehicle.telemetry`,
 * `notification.create`, `charger_id`. A separator is required so bare single words
 * (`jose`, `admin`, `bess1`) are treated as anonymizable names/labels, not machine tokens.
 */
const MACHINE_TOKEN_RE = /^[a-z0-9]+([._:\-/][a-z0-9]+)+$/
/** ALL_CAPS enum values, e.g. `PRECOMMISSIONED`. */
const ENUM_RE = /^[A-Z0-9_]+$/

/** Replaces any email address embedded in free text (descriptions, comments, ticket bodies). */
function scrubEmails(value: string): string {
  if (!value.includes('@')) return value
  return value.replace(EMAIL_RE, (match) => emailAddress(match))
}

// ---------------------------------------------------------------------------
// Coordinates
//
// Latitude/longitude are numbers, but they are location identity — a site's real
// coordinates deanonymize the customer instantly on any map. Points are translated by a
// deterministic offset. When the object identifies a site (siteId/depotId) that id drives
// the offset, so the depot marker, its geofence, and its chargers all shift by the same
// delta and intra-site geometry is preserved exactly. Coordinates without a site id fall
// back to a per-axis grid-cell offset so lone/standalone points are still translated.
// ---------------------------------------------------------------------------

const CELL_SIZE_DEG = 0.5

type Offset = { dLat: number; dLng: number }

/** Maps a hash onto ±[min, max] degrees, never returning a near-zero offset. */
function signedOffset(hash: number, min: number, max: number): number {
  const magnitude = min + (hash % 1000) * ((max - min) / 1000)
  return (hash & 0x10000) === 0 ? magnitude : -magnitude
}

const clampLat = (lat: number): number => Math.max(-85, Math.min(85, lat))
const wrapLng = (lng: number): number => (lng > 180 ? lng - 360 : lng < -180 ? lng + 360 : lng)

/**
 * Preferred offset: derived from a site/depot identifier so every object belonging to a site
 * (the depot marker + geofence, its chargers, parked assets) shifts by the *same* delta and
 * intra-site geometry is preserved exactly — with no grid-cell boundary where co-located points
 * could otherwise split. Different sites get different offsets, which also removes any real
 * relative-geography correlation between sites.
 */
const idOffset = (id: string): Offset => ({
  dLat: signedOffset(fnv1a(`geo-lat|site|${id}`), 1, 4),
  dLng: signedOffset(fnv1a(`geo-lng|site|${id}`), 2, 8)
})

// Fallback per-axis cell offsets, used when an object has no site identifier (lone telemetry
// points, OCPI locations, trip paths). Each axis derives from the coordinate's own 0.5° cell
// so a lone latitude or longitude can still be translated on its own.
const latCellOffset = (lat: number): number => signedOffset(fnv1a(`geo-lat|${Math.floor(lat / CELL_SIZE_DEG)}`), 1, 4)
const lngCellOffset = (lng: number): number => signedOffset(fnv1a(`geo-lng|${Math.floor(lng / CELL_SIZE_DEG)}`), 2, 8)

const translateLatitude = (lat: number): number => clampLat(lat + latCellOffset(lat))
const translateLongitude = (lng: number): number => wrapLng(lng + lngCellOffset(lng))

function cellOffset(lat: number, lng: number): Offset {
  return { dLat: latCellOffset(lat), dLng: lngCellOffset(lng) }
}

/** Latitude/longitude translators bound to a fixed site offset when available, else per-cell. */
const latTranslatorFor = (anchor: Offset | undefined): ((lat: number) => number) =>
  anchor ? (lat) => clampLat(lat + anchor.dLat) : translateLatitude
const lngTranslatorFor = (anchor: Offset | undefined): ((lng: number) => number) =>
  anchor ? (lng) => wrapLng(lng + anchor.dLng) : translateLongitude

type Position = [number, number, ...number[]]

const isPosition = (value: unknown): value is Position =>
  Array.isArray(value) && value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number'

/**
 * Translates GeoJSON-style nested coordinate arrays (positions are `[lng, lat]`).
 * The offset is derived from the first position so a geometry that happens to straddle
 * a grid-cell boundary still moves as one rigid shape.
 */
function translateCoordinates(coordinates: unknown, offset?: { dLat: number; dLng: number }): unknown {
  if (isPosition(coordinates)) {
    const [lng, lat, ...rest] = coordinates
    const { dLat, dLng } = offset ?? cellOffset(lat, lng)
    const newLat = Math.max(-85, Math.min(85, lat + dLat))
    let newLng = lng + dLng
    if (newLng > 180) newLng -= 360
    if (newLng < -180) newLng += 360
    return [newLng, newLat, ...rest]
  }
  if (Array.isArray(coordinates)) {
    const shared = offset ?? findAnchorOffset(coordinates)
    return coordinates.map((entry) => translateCoordinates(entry, shared))
  }
  return coordinates
}

function findAnchorOffset(coordinates: unknown[]): { dLat: number; dLng: number } | undefined {
  for (const entry of coordinates) {
    if (isPosition(entry)) return cellOffset(entry[1], entry[0])
    if (Array.isArray(entry)) {
      const nested = findAnchorOffset(entry)
      if (nested) return nested
    }
  }
  return undefined
}

/** Geofences are stored as JSON-stringified arrays of `[lng, lat]` positions. */
function translateGeofenceString(value: string, anchor?: Offset): string {
  try {
    const positions: unknown = JSON.parse(value)
    if (!Array.isArray(positions)) return value
    return JSON.stringify(translateCoordinates(positions, anchor))
  } catch {
    return value
  }
}

// ---------------------------------------------------------------------------
// Field rules
// ---------------------------------------------------------------------------

type Rule = (value: string, siblings: Record<string, unknown>) => string

/** `address` holds a street address on most models but a full email on email-intake models. */
const smartAddress: Rule = (value) => (value.includes('@') ? emailAddress(value) : streetAddress(value))

/** `createdBy`/`updatedBy` may hold a user ID, an email, or a display name. */
const authorRef: Rule = (value) => {
  if (UUID_RE.test(value)) return value
  if (value.includes('@')) return emailAddress(value)
  if (MACHINE_TOKEN_RE.test(value) || ENUM_RE.test(value)) return value
  return personName(value)
}

/**
 * Generic `name` fields. A `name` that sits next to contact fields belongs to a person and
 * is always anonymized to a person name — even a bare lowercase value like `jose`. Integration
 * provider objects carry `integrationType` alongside `name`; those names are vendor/product
 * brands (Geotab, Payter, …), not customer identity, and must stay intact so demo-mode URL
 * slug lookups (`/settings/integrations/geotab`) still match the anonymized OIP response
 * (ENG-10654). Otherwise the value is left untouched only when it's a machine identifier or
 * enum (so workflow node types, config keys, etc. keep working); anything else becomes a
 * neutral label. Customer-authored Integration *instance* names lack `integrationType` as a
 * sibling and continue to anonymize.
 */
const smartName: Rule = (value, siblings) => {
  const personLike = ['email', 'phoneNumber', 'contactEmail', 'rfidTags'].some((key) => key in siblings)
  if (personLike) return personName(value)
  // Vendor brand on SparseIntegrationProvider / IntegrationProviderReference — not PII.
  if ('integrationType' in siblings) return value
  if (MACHINE_TOKEN_RE.test(value) || ENUM_RE.test(value)) return value
  return genericLabel(value)
}

/**
 * Every string field the anonymizer rewrites, keyed by exact JSON key name. Keys not
 * listed here pass through (after embedded-email scrubbing), so numeric values, enums,
 * timestamps, and machine identifiers are never altered.
 */
const STRING_RULES: Record<string, Rule> = {
  // Organizations / fleets / businesses
  organizationNm: organizationName,
  organizationName: organizationName,
  orgName: organizationName,
  parentOrganizationNm: organizationName,
  payorOrganizationName: organizationName,
  granteeOrganizationName: organizationName,
  counterpartyOrgName: organizationName,
  fleetNm: organizationName,
  fleetName: organizationName,
  businessName: organizationName,
  customerName: organizationName,
  accountName: organizationName,
  payorName: organizationName,
  merchantName: organizationName,
  administeredByOrgName: organizationName,

  // Sites / depots / locations of interest
  depotNm: siteName,
  depotName: siteName,
  siteName,
  siteNm: siteName,
  loiNm: genericLabel,

  // Node/group labels. Denormalized onto chargers and transactions as `nodeName`/`groupName`;
  // uses genericLabel to match how a node's own `name` field anonymizes, so the same node reads
  // consistently across the chargers table, transactions, and the site architecture view.
  nodeName: genericLabel,
  groupName: genericLabel,

  // Chargers / vehicles / telematics devices
  chargerName,
  vehicleNm: vehicleName,
  vehicleName: vehicleName,
  vehicleDisplayNm: vehicleName,
  deviceName: vehicleName,

  // SynopLinks: nickName is a customer-assigned label; thingName is the device identifier.
  nickName: genericLabel,
  thingName: token,

  // People
  name: smartName,
  firstName,
  lastName,
  firstNm: firstName,
  lastNm: lastName,
  fullName: personName,
  driverName: personName,
  userName: personName,
  reporterName: personName,
  authorName: personName,
  contactName: personName,
  primaryContactName: personName,
  consignee: personName,
  approvedByName: personName,
  lastModifiedByUserName: personName,
  createdBy: authorRef,
  createdByName: authorRef,
  updatedBy: authorRef,
  modifiedBy: authorRef,

  // Ambiguous display names (org or person depending on context) — smartName decides.
  displayName: smartName,
  requestedName: smartName,

  // Billing product / price / rate / line-item labels and shared-asset names — all
  // free-form and customer-authored (tax rate names can embed customer/site names too).
  productName: genericLabel,
  productNm: genericLabel,
  priceName: genericLabel,
  rateName: genericLabel,
  rateNm: genericLabel,
  lineItemNm: genericLabel,
  taxRateName: genericLabel,
  assetName: genericLabel,

  // Emails
  email: emailAddress,
  emailAddress,
  userEmail: emailAddress,
  contactEmail: emailAddress,
  primaryContactEmail: emailAddress,
  payorEmail: emailAddress,
  reporterEmail: emailAddress,
  authorEmail: emailAddress,

  // Phones
  phone: phoneNumber,
  phoneNumber,
  phone_number: phoneNumber,
  contactPhone: phoneNumber,
  primaryContactPhone: phoneNumber,
  faxNumber: phoneNumber,

  // Street addresses
  address: smartAddress,
  address1: streetAddress,
  address2: streetAddress,
  addressLine1: streetAddress,
  addressLine2: streetAddress,
  line1: streetAddress,
  line2: streetAddress,
  billingLine1: streetAddress,
  billingLine2: streetAddress,
  street: streetAddress,
  streetAddress,

  // Cities / postal codes (country, region, and timezone stay real — they're coarse
  // and keeping them makes time-series data remain plausible)
  city: cityName,
  billingCity: cityName,
  town: cityName,
  postalCode,
  billingPostalCode: postalCode,
  zipCode: postalCode,
  zip: postalCode,
  postcode: postalCode,

  // Vehicle / hardware identifiers
  vin,
  vehicleVin: vin,
  chargePointSerialNumber: token,
  chargeBoxSerialNumber: token,
  meterSerialNumber: token,
  serialNumber: token,
  iccid: token,
  imsi: token,
  imei: token,
  externalId: token,
  externalDeviceId: token,
  externalVehicleId: token,
  ocppTagValue: token,
  ocppTag: token,
  idTag: token,
  authTag: token,
  normalizedAuthTag: token,
  driverExternalId: token,
  macAddress,

  // URLs and SSO/tenant identity
  loginUrl: url,
  // websocketUrl is a scheme-less host/path (OcppConnection prepends wss://); use a bare host
  // so the copied OCPP URL doesn't become malformed like `wss://https://...`.
  websocketUrl: domainName,
  endpointAddress: url,
  iosAppStoreUrl: url,
  androidPlayStoreUrl: url,
  auth0Tenant: token,
  ssoConnectionId: token,
  appClientId: token,
  domain: domainName,
  tenantDomain: domainName,

  // Free-text fields (descriptions, notes, memos, terms, labels). These can contain
  // arbitrary customer identifiers — product/agreement descriptions on the billing pages
  // embed real place and customer names — so they're replaced with a neutral placeholder
  // rather than preserved. Applies wherever these keys appear, not only in billing.
  description: descriptionText,
  productDescription: descriptionText,
  platformFeeDescription: descriptionText,
  note: descriptionText,
  notes: descriptionText,
  memo: descriptionText,
  terms: descriptionText,
  termsAndConditions: descriptionText,
  usageStartNote: descriptionText,
  usageEndNote: descriptionText,
  processingFeeNote: descriptionText,
  label: descriptionText
  // Note: `geofence` and latitude/longitude are handled directly in transformObject (not here)
  // because they need the object's site anchor, not the generic (value, siblings) rule shape.
}

/** Keys removed entirely so branded assets fall back to Synop defaults. */
const DROPPED_KEYS = new Set(['logoUrl', 'iconUrl', 'logoUrlLight', 'logoUrlDark', 'iconUrlLight', 'iconUrlDark'])

/** Keys carrying customer email/SSO domains, as comma-separated strings or string arrays. */
const DOMAIN_LIST_KEYS = new Set(['ssoEmailDomains', 'domainAliases', 'allowedDomains', 'allowedSenderDomains'])

/** Keys whose object values are GeoJSON-like geometry (`{ type, coordinates }`). */
const GEOMETRY_KEYS = new Set(['location', 'geometry', 'geoJson', 'geojson'])

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && value.constructor === Object

/** Bare (`lat`) or suffixed (`latitude`, `startLatitude`) latitude keys. */
const LAT_KEY_RE = /^lat$|latitude$/i
/** Bare (`lng`/`lon`/`long`) or suffixed (`longitude`, `startLongitude`) longitude keys. */
const LNG_KEY_RE = /^(?:lng|lon|long)$|longitude$/i

/** Source-attribute shape used by fleet telemetry payloads: `{ value: number, units, ... }`. */
const isNumericSourceAttribute = (value: unknown): value is Record<string, unknown> & { value: number } =>
  isPlainObject(value) && typeof value.value === 'number'

/** Source-attribute shape carrying a string, e.g. `CurrentLocation.street` = `{ value: string, units, ... }`. */
const isStringSourceAttribute = (value: unknown): value is Record<string, unknown> & { value: string } =>
  isPlainObject(value) && typeof value.value === 'string'

/**
 * Translates a single latitude or longitude field. Handles a plain number, a numeric string
 * (OCPI models type coordinates as strings — re-emitted as a string), and a numeric
 * source-attribute object (`{ value, units, ... }`). Returns undefined when the value isn't a
 * coordinate (e.g. null/missing), so the caller can fall through. Each axis is independent, so
 * a lone latitude with no longitude sibling is still translated.
 */
function translateCoordinateField(value: unknown, translate: (n: number) => number): unknown {
  if (typeof value === 'number') return translate(value)
  if (typeof value === 'string') {
    const n = Number(value)
    return value.trim() !== '' && Number.isFinite(n) ? String(translate(n)) : undefined
  }
  if (isNumericSourceAttribute(value)) return { ...transformObject(value), value: translate(value.value) }
  return undefined
}

/**
 * A site/depot identifier on the object, used to anchor coordinate translation so all of a
 * site's objects (depot marker + geofence, chargers, assets) move by the same offset. `siteId`
 * and `depotId` are the same entity in this codebase (depot is the legacy name); the depot
 * object carries its own `depotId` and chargers carry the parent `depotId`/`siteId`, so keying
 * on this value aligns them.
 */
function siteAnchorFor(obj: Record<string, unknown>): Offset | undefined {
  const id = [obj.siteId, obj.depotId].find((v) => typeof v === 'string' && v !== '')
  return typeof id === 'string' ? idOffset(id) : undefined
}

function transformObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  // When the object identifies a site, every coordinate/geofence in it shifts by one shared
  // offset (perfectly rigid, no grid-cell boundary); otherwise each coordinate falls back to a
  // per-axis cell offset so lone/standalone coordinates are still translated.
  const anchor = siteAnchorFor(obj)
  const translateLat = latTranslatorFor(anchor)
  const translateLng = lngTranslatorFor(anchor)

  for (const [key, value] of Object.entries(obj)) {
    if (DROPPED_KEYS.has(key)) {
      out[key] = undefined
      continue
    }

    // Latitude/longitude fields cover plain numbers, string coordinates (OCPI), and numeric
    // source-attributes. Non-coordinate values (null/missing) fall through unchanged.
    if (LAT_KEY_RE.test(key)) {
      const translated = translateCoordinateField(value, translateLat)
      if (translated !== undefined) {
        out[key] = translated
        continue
      }
    } else if (LNG_KEY_RE.test(key)) {
      const translated = translateCoordinateField(value, translateLng)
      if (translated !== undefined) {
        out[key] = translated
        continue
      }
    }

    if (key === 'geofence' && typeof value === 'string' && value !== '') {
      out[key] = translateGeofenceString(value, anchor)
      continue
    }

    // Domain lists arrive as comma-separated strings (`ssoEmailDomains`) or string
    // arrays (`domainAliases`, `allowedDomains`, `allowedSenderDomains`).
    if (DOMAIN_LIST_KEYS.has(key)) {
      if (typeof value === 'string' && value !== '') {
        out[key] = value
          .split(',')
          .map((entry) => domainName(entry.trim()))
          .join(',')
        continue
      }
      if (Array.isArray(value)) {
        out[key] = value.map((entry) => (typeof entry === 'string' ? domainName(entry) : transform(entry)))
        continue
      }
    }

    if (GEOMETRY_KEYS.has(key) && isPlainObject(value) && 'coordinates' in value) {
      out[key] = { ...value, coordinates: translateCoordinates(value.coordinates, anchor) }
      continue
    }

    const rule = STRING_RULES[key]
    if (rule) {
      if (typeof value === 'string' && value !== '') {
        out[key] = rule(value, obj)
        continue
      }
      // Fleet telemetry wraps some of these (e.g. `street`) as source-attribute objects
      // whose `.value` holds the real string; rewrite that and keep the metadata.
      if (isStringSourceAttribute(value) && value.value !== '') {
        out[key] = { ...transformObject(value), value: rule(value.value, obj) }
        continue
      }
    }

    out[key] = transform(value)
  }

  return out
}

function transform(value: unknown): unknown {
  if (typeof value === 'string') return scrubEmails(value)
  if (Array.isArray(value)) return value.map(transform)
  if (isPlainObject(value)) return transformObject(value)
  return value
}

/**
 * Returns an anonymized copy of a parsed JSON response body. Never mutates the input.
 * Non-JSON payloads (blobs, array buffers, class instances) pass through unchanged.
 */
// eslint-disable-next-line import/prefer-default-export
export function anonymizeJson<T>(data: T): T {
  return transform(data) as T
}
